import React, { useCallback, useState } from "react";
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  signInWithCredential,
} from "firebase/auth";
import "./GoogleSignIn.css";
import Button from "../components/Button/Button";
import { FormRow } from "../pages/Common/LandingPage/SectionContent/AboutContent";
import { useTheme } from "@emotion/react";
import { useUserControls } from "../context/UserControlsProvider";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { isIOSNative, isNativePlatform } from "../utils/platform";

type SnackbarFn = (options: {
  message: string;
  severity?: "success" | "info" | "warning" | "error";
  duration?: number;
}) => void;

interface SignInWithGoogleOptions {
  onSuccessfulLogin?: () => void;
  showSnackbar?: SnackbarFn;
}

export const signInWithGoogle = async ({
  onSuccessfulLogin,
  showSnackbar,
}: SignInWithGoogleOptions = {}) => {
  console.log("Google Sign-In button clicked!");

  const auth = getAuth();
  const provider = new GoogleAuthProvider();
  provider.addScope("profile");
  provider.addScope("email");
  provider.setCustomParameters({
    prompt: "select_account",
  });

  try {
    if (isNativePlatform()) {
      const nativeResult = await FirebaseAuthentication.signInWithGoogle({
        skipNativeAuth: true,
      });
      const idToken = nativeResult.credential?.idToken;
      const accessToken = nativeResult.credential?.accessToken;
      if (!idToken) {
        throw new Error("Missing Google ID token from native credential.");
      }
      const credential = GoogleAuthProvider.credential(idToken, accessToken);
      const userCredential = await signInWithCredential(auth, credential);
      const user = userCredential.user;
      console.log("[GoogleSignIn] Native user signed in:", user.uid);
      showSnackbar?.({
        message: "Signed in with Google.",
        severity: "success",
      });
      onSuccessfulLogin?.();
      return userCredential;
    }

    const result = await signInWithPopup(auth, provider);
    const googleCredential = GoogleAuthProvider.credentialFromResult(result);
    const token = googleCredential?.accessToken;
    const idToken = await result.user.getIdToken();
    const user = result.user;
    console.log("User signed in:", user);
    console.log("Google access token:", token);
    console.log("Firebase ID token:", idToken);

    showSnackbar?.({
      message: "Signed in with Google.",
      severity: "success",
    });
    onSuccessfulLogin?.();
    return result;
  } catch (error: any) {
    console.error("Error signing in with Google: ", error);
    const code = (error?.code ?? "").toString();
    const message = deriveGoogleErrorMessage(code, error?.message);
    showSnackbar?.({
      message,
      severity: "error",
      duration: 6000,
    });
    throw error;
  }
};

const deriveGoogleErrorMessage = (code: string, fallback?: string) => {
  if (code.includes("operation-not-supported")) {
    return "Google sign-in is unavailable in this environment.";
  }
  if (code.includes("popup-blocked")) {
    return "Your browser blocked the Google sign-in window. Allow popups and retry.";
  }
  if (code.includes("popup-closed-by-user")) {
    return "Google sign-in was closed before completing.";
  }
  if (code.includes("argument-error")) {
    return "Native Google sign-in is not fully configured. Confirm the reversed client ID and SHA keys.";
  }
  return fallback || "Unable to sign in with Google. Please try again.";
};

interface GoogleSignInButtonProps {
  onSuccessfulLogin?: () => void;
}

const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  onSuccessfulLogin,
}) => {
  if (isIOSNative()) {
    return null;
  }
  const theme: any = useTheme();
  const { showSnackbar } = useUserControls();
  const [loading, setLoading] = useState(false);

  const handleClick = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    try {
      await signInWithGoogle({ onSuccessfulLogin, showSnackbar });
    } catch {
      // Notification handled upstream
    } finally {
      setLoading(false);
    }
  }, [loading, onSuccessfulLogin, showSnackbar]);

  return (
    <FormRow labelWidth="100%">
      <Button
        size="sm"
        onClick={handleClick}
        disabled={loading}
        aria-busy={loading}
        aria-label="Sign in with Google"
        style={{
          width: "100%",
          padding: ".75rem",
          backgroundColor: `color-mix(in srgb, ${theme.background}, ${theme.foreground} 5%)`,
          color: theme.foreground,
        }}
      >
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg"
          alt="Google"
          style={{ height: 20, marginRight: 8 }}
        />
        {loading ? "Signing in..." : "Sign in with Google"}
      </Button>
    </FormRow>
  );
};

export default GoogleSignInButton;
